import { v4 as uuid } from "uuid";
import { TClosedRange, TImage, UnionOmit, IBinding } from "./types";
import { IThreediumTemplate } from "./threedium";
import {
    TCustomizationSelection,
    CustomizationSelectionType,
    CustomizationSelectionMap,
    TMetaCustomizationSelection,
    TPrintedImageCustomizationSelection,
    MetaSelectionType,
    PrintedTextOptionValueKey,
    TPrintedTextCustomizationSelection,
    TColorSwatchCustomizationSelection,
    TColorPickerCustomizationSelection,
    TSelectableCustomizationSelection,
    TCustomizationSelectionTextValue,
    TCustomizationSelectionSelectableValue,
    TCustomizationSelectionImageValue,
    TCustomizationSelectionColorSwatchValue,
    TCustomizationSelectionColorPickerValue,
    TSelectableValue,
    TPrintedTextChildAttributeSelection,
    TPrintedTextOptionCustomizationSelection,
} from "./customization-selection.types";
import { assertExhaustiveSwitch } from "components/utils/validations";
import { IProduct } from "records/item/item";
import { ILineItem } from "../order-types";
import {
    CustomizationSettingsDataType,
    IProductCustomizationImageType,
    IProductCustomizationPrint,
} from "../../records/brand/brand";
import { ICustomizationImageEntityAttribute } from "../../epics/customizations-epic";
import { ChangeEvent } from "react";
import { IImageTagFilter, TagDataTypes } from "./customization-image-tag-schema";
import { IFetchParams } from "../../epics/base-epic";

export type CommonProps = {
    [key: string]: any;
};

export type { IBinding };

export type PrintType = string;
export type PrintedField = "PRINT_TYPE";
export type TTagInputValue = string | string[] | boolean | number;

type PickFields<T, FieldType> = {
    [Key in keyof T as T[Key] extends undefined | FieldType ? Key : never]: T[Key];
};

export const AttributeFields: Record<string, undefined | keyof PickFields<TCustomizationSelection, string>> =
    Object.freeze({
        PRINT_TYPE: "printType",
    });

export interface ISettingsValue {
    _id: string;
    name: string;
}

export type IPrintTypeId = string;
export type IPrintType = string;
export type ProductCustomizationSettingsMap = Map<IPrintTypeId, IPrintType>;

export type OverlayType = "UNDEFINED" | "IMAGE" | "TEXT";

export interface ColorValue {
    rgb: string;
}

export interface IValueOption {
    _id: string;
    name: string;
    string?: string;
    integer?: number;
    number?: number;
    color?: ColorValue;
    binding?: IBinding;
}

export interface ISelectableValue {
    defaultValueIndex?: number;
    values: IValueOption[];
}

export interface IPrintedImage {
    permissions: IPrintedImagePermission[];
}

export enum ImagePermissionType {
    BUYER,
    BRAND,
}

export interface IPrintedImagePermission {
    _id: string;
    type: keyof typeof ImagePermissionType;
    name?: string;
    canUploadImage: boolean;
}

export interface IPrintedText {
    defaultValue?: string;
    characterLimit: TClosedRange;
    font?: ISelectableValue;
    fontSize?: ISelectableValue;
    color?: IColorAttribute;
}

export interface IPrintedAttribute {
    types: PrintType[];
    image?: IPrintedImage;
    text?: IPrintedText;
}

export interface IColorAttribute {
    picker?: ColorValue;
    swatch?: ISelectableValue;
}

export interface IImageUrlData {
    url: string;
    filename: string;
    imageId: string;
}

export interface IAttribute {
    _id: string;
    externalId: string;
    name: string;
    isRequired?: boolean;
    tooltip?: string;
    description?: string;
    childAttributes?: IAttribute[];
    image?: TImage;
    printed?: IPrintedAttribute;
    color?: IColorAttribute;
    selectable?: ISelectableValue;
    metadata?: IMetadata;
    binding?: IBinding;
}

export interface IMetadata {
    selectable?: IMetaSelectable;
    text?: IMetaText;
    visibilityRules?: IVisibilityRule[];
}

export interface IVisibilityRule {
    field?: keyof typeof AttributeFields;
    values?: PrintType[];
}

export interface IMetaText {
    defaultValue: string;
}

export interface IMetaSelectable {
    values: IValueOption[];
}

export interface IThreediumModel {
    distId: string;
    solutionName: string;
    projectName: string;
    solutionId: string;
    version: string;
}

export interface IVisualizer {
    _id: string;
    brandId?: string;
    name: string;
    description?: string;
    image?: TImage;
    attributes: IVisualizerAttribute[];
    revision: string | number;
    threedium?: IThreediumModel;
    planar2d?: IPlanar2dModel;
    createdAt?: Date | string;
    updatedAt?: Date | string;
}

export interface IPlanar2dModel {
    backgroundImages: IBackgroundImage[];
    locations: ILocation[];
}

export interface IBackgroundImage {
    selector: string;
    keys: string[];
    image: TImage;
}

export interface ILocation {
    _id: string;
    x: string;
    y: string;
    width: string;
    height: string;
}

export interface Planar2dAttribute {
    backgroundImageSelector: string;
    overlay?: IPlanarOverlay;
    background?: IBackgroundItem;
}

export interface IBackgroundItem {
    type: string;
    values: Array<IBackgroundValue>;
}

export interface IBackgroundValue {
    _id: string;
    name: string;
}

export interface IPlanarOverlay {
    type: string;
    locationId: string;
}

export interface IVisualizerAttribute {
    _id: string;
    name: string;
    description?: string;
    image?: TImage;
    threedium?: IThreediumAttribute;
    planar2d?: IPlanar2dAttribute;
}

export interface IPlanar2dAttribute {
    backgroundImageSelector: string;
    overlay?: IPlanar2dOverlayAttribute;
    background?: IPlanar2dBackgroundAttribute;
}

export interface IPlanar2dBackgroundAttribute {
    type: BackgroundAttributeType;
    values: IBackgroundValue[];
}

export interface IProductDataEntry {
    lineItem?: ILineItem;
    product: IProduct;
}

export interface ITagModalData {
    name: string;
    externalId: string;
    type: TagDataTypes;
    filter?: IImageTagFilter;
}

export interface ILogoDownloadData {
    filename: string;
    url: string;
}

export interface ILogoMerchantData {
    id: string;
    name: string;
}

export interface IDownloadURL {
    urlsList: ILogoDownloadData[];
}

export interface ILogoDataEntry {
    id: string;
    logo: string;
    name: string;
    merchantId: string;
    tagSchemaData: ICustomizationImageEntityAttribute[];
    tagsModalData: ITagModalData[];
    createTime?: TTime;
    updateTime?: TTime;
    approvedMerchants?: ILogoMerchantData[];
    pendingMerchants?: ILogoMerchantData[];
    rejectedMerchants?: ILogoMerchantData[];
    downloadURL?: IDownloadURL;
}

export enum LogoStatusType {
    PENDING = "pending",
    APPROVED = "approved",
    REJECTED = "rejected",
    ALLMERCHANTS = "allmerchants",
}

export enum ImageTagStatusListName {
    APPROVED = "__approved_for_merchants",
    REJECTED = "__rejected_for_merchants",
}

export interface IImageUpdateData {
    id?: string;
    name?: string;
    forMerchantId?: string;
    tags?: TTagUpdates;
    status?: CustomizationImageStatus;
    createTime?: TTime;
    updateTime?: TTime;
}

export interface TTime {
    seconds: number;
    nanos: number;
}

export enum CustomizationImageAttributeKind {
    STRING = 0,
    FLOAT = 5,
    BOOL = 10,
    ARRAY = 11,
    NULL = 15,
}

export enum BackgroundAttributeType {
    COLOR = "COLOR",
    PART = "PART",
}

export enum OverlayAttributeType {
    IMAGE = "IMAGE",
    TEXT = "TEXT",
}

export interface IPlanar2dOverlayAttribute {
    type: OverlayAttributeType;
    locationId: string;
}

export interface VisualizerTemplate extends IVisualizer {
    attributesMap?: { [key: string]: IVisualizerAttribute };
}

export interface IThreediumAttribute {
    cameraTransition?: string;
    overlay?: IOverlayAttribute;
    material?: IMaterialAttribute;
    parts?: IPart[];
}

export interface IOverlayAttribute {
    overlay: string;
    entry: string;
    type: OverlayType;
}

export interface IMaterialAttribute {
    material: string;
    parts: string[];
}

export interface IPart {
    _id?: string;
    name: string;
    part?: string;
}

export interface ICustomizationTemplate extends CommonProps {
    _id: string;
    name: string;
    description: string;
    attributeLimit?: TClosedRange;
    attributes: IAttribute[];
    thumbnail?: TImage;
    visualizers?: IVisualizer[];
    image?: TImage;
}

export interface IThreediumCustomization {
    model?: IThreediumModel;
    threedium?: IThreediumAttribute;
    planar2d?: IPlanar2dAttribute;
    planarData?: IPlanar2dModel;
    binding: IBinding;
    selection?: TCustomizationSelection;
    defaults?: TCustomizationSelection;
    attribute?: IAttribute;
    visualizerAttribute?: IVisualizerAttribute;
    visualizer?: IVisualizer;
}

export type TBulkCustomizationQueryFilter = {
    _id?: string[];
    currency_code?: string;
    __advanced_pricing_currency_code?: string;
    __warehouses?: string[];
} & Record<string, (string | number | boolean) | (string | number | boolean)[]>;

type TBulkCustomizationQuerySort = {
    sort: string;
    sortOrder: "asc" | "desc";
};

export interface IBulkCustomizationQuery {
    isPDP?: boolean;
    templateId: string;
    filters?: TBulkCustomizationQueryFilter;
    imageIds: string[];
    search?: string;
    limit: number;
    lastId: string | undefined;
    sort?: TBulkCustomizationQuerySort;
}

export interface ITagDataUpdates {
    name?: string;
    tags?: TTagUpdates;
}

export type TTagUpdateMap = Map<string, TTagInputValue>;
export type TTagUpdates = { name: string; value: string | number | boolean }[];

export interface ICustomizationData {
    templateId: string;
    customizations: TCustomizationSelection[];
    orderId: string;
}

export interface IGridEntryCustomization {
    customization: ICustomizationData;
}

export interface CustomizationImageFilterDictionary {
    [key: string]: string | number | boolean;
}

export type TBrandSettingsTypeName = Pick<IProductCustomizationPrint | IProductCustomizationImageType, "name">;

export type TBrandSettingsUpdatesData = {
    [key: string]: TBrandSettingsTypeName;
};

export enum TypeUpdateAction {
    DELETE = "delete",
    UPDATE = "update",
    CREATE = "create",
}

export enum CustomizationImageStatus {
    APPROVED = "approved",
    REJECTED = "rejected",
    PENDING = "pending",
    BRANDAPPROVED = "brandapproved",
}

export interface IBrandSettingsTypesUpdate {
    [TypeUpdateAction.DELETE]?: string[];
    [TypeUpdateAction.UPDATE]?: TBrandSettingsUpdatesData;
    [TypeUpdateAction.CREATE]?: CustomizationSettingsDataType[];
}

export interface TCustomizationSettingsUpdate {
    [TypeUpdateAction.DELETE]?: Set<string>;
    [TypeUpdateAction.UPDATE]?: Map<string, CustomizationSettingsDataType>;
    [TypeUpdateAction.CREATE]?: Map<string, CustomizationSettingsDataType>;
}

export interface IBrandSettingsTypesUpdateData {
    prints: IBrandSettingsTypesUpdate;
    image_types: IBrandSettingsTypesUpdate;
}

export interface ICreateProofActionPayload {
    orderId: string;
    body: ICreateProofBody;
    file: File;
    lineItemId: string;
}

export interface ICreateProofLineItem {
    id: string;
    file_size: number;
}

export interface IUploadProofLineItem {
    id: string;
    uploadId?: string;
}

export interface ICreateProofBody {
    line_items: ICreateProofLineItem[];
}

export interface IUploadProofBody {
    line_items: IUploadProofLineItem[];
}

export enum ProofUploadMethod {
    PUT = "PUT",
    POST = "POST",
}

export interface IProofUploadHeaders {
    "Content-Disposition": string;
    "Content-Length": string;
    "Content-Type": string;
}

export interface IUploadPayload {
    id: string;
    upload: {
        id: string;
        url: string;
        method: ProofUploadMethod;
        headers: IProofUploadHeaders;
        file: File;
    };
}

export interface IInvalidProduct {
    _id: string;
    name: string;
    style_number: string;
    season: string;
    color: string;
    __product_customizations_template_id?: string;
}

export interface ICheckCustomizationPayload {
    isValid: boolean;
    invalidProducts: IInvalidProduct[];
}

export type TCreateProofParams = IFetchParams & ICreateProofActionPayload;
export type TChangeEvent = ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

export type TProductIds = string[];
export type TLineItemIds = string[];

export const getSelectableValue = (value?: IValueOption): string =>
    value?.string ?? value?.number?.toString() ?? value?.integer?.toString() ?? value?.color?.rgb ?? "";

export const getValueKey = (value: IValueOption): string => value._id ?? getSelectableValue(value) ?? value.name;

const selection = <Type, Extensions>(type: Type, attribute: IAttribute, extensions: Extensions) => ({
    _id: undefined,
    type,
    attributeId: attribute._id,
    externalId: attribute.externalId,
    label: attribute.name,
    ...extensions,
});

const bindableSelection = <Type, Extensions>(type: Type, attribute: IAttribute, extensions: Extensions) => ({
    ...selection(type, attribute, extensions),
    target: attribute.binding,
});

const textValue = (value: string) => ({ value, displayValue: value });
const selectableValue = (option?: IValueOption) => ({
    value: getSelectableValue(option),
    displayValue: option?.name ?? "",
    valueId: option?._id ?? "",
});

const bindableSelectableValue = (option?: IValueOption, attribute?: IAttribute) => ({
    ...selectableValue(option),
    target: option?.binding ?? attribute?.binding,
});

const noneValueOption = Object.freeze({
    _id: "",
    name: "None",
    string: "__none__",
});

export const NoneValueOption: IValueOption & typeof noneValueOption = noneValueOption;

export const selectableCustomizationSelection = (
    selectable: ISelectableValue,
    attribute: IAttribute,
): TSelectableCustomizationSelection | undefined => {
    const { values, defaultValueIndex } = selectable;
    let index = defaultValueIndex;
    if (typeof index !== "number" && attribute.isRequired) {
        index = 0;
    }

    const defaultOption: IValueOption | undefined = values[index ?? -1] ?? NoneValueOption;

    return selection(
        CustomizationSelectionType.SELECTABLE as const,
        attribute,
        bindableSelectableValue(defaultOption, attribute),
    );
};

export const colorPickerCustomizationSelection = (
    picker: ColorValue,
    attribute: IAttribute,
): TColorPickerCustomizationSelection | undefined => {
    const rgb = picker?.rgb;
    if (!rgb) {
        return;
    }

    return bindableSelection(CustomizationSelectionType.COLOR_PICKER as const, attribute, textValue(rgb));
};

export const colorSwatchCustomizationSelection = (
    swatch: ISelectableValue,
    attribute: IAttribute,
): TColorSwatchCustomizationSelection | undefined => {
    const defaultOption = swatch.values[swatch.defaultValueIndex ?? -1];
    if (!defaultOption) {
        return;
    }

    return selection(
        CustomizationSelectionType.COLOR_SWATCH as const,
        attribute,
        bindableSelectableValue(defaultOption, attribute),
    );
};

const textOption = <T, Key extends PrintedTextOptionValueKey>(nested: T, label: string, valueKey: Key) => ({
    ...nested,
    label,
    valueKey,
    target: undefined,
});

export const printedTextCustomizationSelection = (
    printedText: IPrintedText,
    childAttributes: IAttribute[],
    printTypes: PrintType[],
    attribute: IAttribute,
): TPrintedTextCustomizationSelection | undefined => {
    const { defaultValue, font, fontSize, color } = printedText;
    if (!printTypes.length) {
        return;
    }

    const [defaultPrintType] = printTypes;
    const result = bindableSelection(CustomizationSelectionType.PRINTED_TEXT as const, attribute, {
        ...textValue(defaultValue ?? ""),
        printType: defaultPrintType,
        nestedValues: [] as TPrintedTextChildAttributeSelection[],
    });

    const nestedValues = result.nestedValues;

    if (font) {
        const nested = selectableCustomizationSelection(font, attribute);
        if (nested) {
            nestedValues.push(textOption(nested, "Font", PrintedTextOptionValueKey.FONT as const));
        }
    }
    if (fontSize) {
        const nested = selectableCustomizationSelection(fontSize, attribute);
        if (nested) {
            nestedValues.push(textOption(nested, "Font Size", PrintedTextOptionValueKey.FONT_SIZE as const));
        }
    }

    const { picker, swatch } = color ?? {};
    if (picker) {
        const nested = colorPickerCustomizationSelection(picker, attribute);
        if (nested) {
            nestedValues.push(textOption(nested, "Color", PrintedTextOptionValueKey.COLOR_PICKER as const));
        }
    }

    if (swatch) {
        const nested = colorSwatchCustomizationSelection(swatch, attribute);
        if (nested) {
            nestedValues.push(textOption(nested, "Color", PrintedTextOptionValueKey.COLOR_SWATCH as const));
        }
    }

    nestedValues.push(
        ...getDefaultMetaCustomizationSelections(childAttributes, {
            parentSelection: result,
            filterByVisibility: true,
        }),
    );

    return result;
};

export const printedImageCustomizationSelection = (
    childAttributes: IAttribute[],
    printTypes: PrintType[],
    attribute: IAttribute,
): TPrintedImageCustomizationSelection | undefined => {
    if (!printTypes.length) {
        return;
    }

    const [defaultPrintType] = printTypes;

    const selection = bindableSelection(CustomizationSelectionType.PRINTED_IMAGE as const, attribute, {
        value: "",
        displayValue: "",
        printType: defaultPrintType,
        nestedValues: [] as TMetaCustomizationSelection[],
    });

    selection.nestedValues.push(
        ...getDefaultMetaCustomizationSelections(childAttributes, {
            parentSelection: selection,
            filterByVisibility: true,
        }),
    );

    return selection;
};

export const customizationSelectionDefaultValue = (attribute: IAttribute): TCustomizationSelection | undefined => {
    const { selectable, color, printed, childAttributes = [] } = attribute;

    if (selectable) {
        return selectableCustomizationSelection(selectable, attribute);
    }

    const { picker, swatch } = color ?? {};
    if (picker) {
        return colorPickerCustomizationSelection(picker, attribute);
    }

    if (swatch) {
        return colorSwatchCustomizationSelection(swatch, attribute);
    }

    const { text, image, types = [] } = printed ?? {};
    if (text) {
        return printedTextCustomizationSelection(text, childAttributes, types, attribute);
    }

    if (image) {
        return printedImageCustomizationSelection(childAttributes, types, attribute);
    }

    return undefined;
};

const getVisibilityRules = (
    visibilityRules: IVisibilityRule[],
    printSettingsMap?: ProductCustomizationSettingsMap,
): IVisibilityRule[] => {
    if (!printSettingsMap) {
        return visibilityRules;
    }

    return visibilityRules.map((visibilityRule: IVisibilityRule): IVisibilityRule => {
        const ruleValues = visibilityRule?.values?.map((ruleValue: PrintType): PrintType => {
            return printSettingsMap.get(ruleValue) ?? ruleValue;
        });

        return {
            field: visibilityRule.field,
            values: ruleValues ?? [],
        };
    });
};

export const getDefaultMetaCustomizationSelections = (
    childAttributes: IAttribute[],
    options?: {
        parentSelection: TPrintedImageCustomizationSelection | TPrintedTextCustomizationSelection;
        filterByVisibility: boolean;
    },
): TMetaCustomizationSelection[] =>
    childAttributes.reduce((acc, attribute, index) => {
        let nested: UnionOmit<TMetaCustomizationSelection, "valueKey"> | undefined;

        const metadata = attribute.metadata;
        if (!metadata) {
            return acc;
        }

        const { text, selectable, visibilityRules = [] } = metadata;
        if (text) {
            nested = selection(MetaSelectionType.TEXT as const, attribute, textValue(text.defaultValue));
        }

        if (selectable) {
            const { values } = selectable;
            const [defaultValue] = values;
            nested = selection(
                MetaSelectionType.SELECTABLE as const,
                attribute,
                bindableSelectableValue(defaultValue, attribute),
            );
        }

        if (!nested) {
            return acc;
        }

        const shouldPush =
            !options ||
            (options.filterByVisibility &&
                !!nested.value &&
                checkVisibilityRules(options.parentSelection, visibilityRules));

        if (shouldPush) {
            acc.push({ ...nested, valueKey: `${index}` });
        }

        return acc;
    }, [] as TMetaCustomizationSelection[]);

export const checkVisibilityRules = (
    attributeValue: TPrintedImageCustomizationSelection | TPrintedTextCustomizationSelection | undefined,
    visibilityRules: IVisibilityRule[] | undefined = [],
    printSettingsMap?: ProductCustomizationSettingsMap,
): boolean => {
    if (!visibilityRules.length || !attributeValue) {
        return true;
    }

    const currentVisibilityRules = getVisibilityRules(visibilityRules, printSettingsMap);

    return currentVisibilityRules.every((rule) => {
        const { field = "", values } = rule;
        const attributeField = AttributeFields[field];
        if (!attributeField) {
            return false;
        }

        const fieldValue = attributeValue[attributeField];

        return !fieldValue ? false : !!values?.includes(fieldValue);
    });
};

const customizationSelectionMap =
    (valueCustomizer: (attribute: IAttribute, selection: TCustomizationSelection) => void) =>
    (attributes: IAttribute[] = []): CustomizationSelectionMap =>
        attributes.reduce((map, attribute) => {
            const selection = customizationSelectionDefaultValue(attribute);
            if (selection) {
                valueCustomizer(attribute, selection);
                map.set(attribute._id, selection);
            }

            return map;
        }, new CustomizationSelectionMap());

export const customizationSelectionDefaultValueMap = customizationSelectionMap((attribute, selection) => {});

export const customizationSelectionUnsetDefaultValueMap = customizationSelectionMap((attribute, selection) => {
    if (attribute.isRequired) {
        return;
    }

    switch (selection.type) {
        case CustomizationSelectionType.COLOR_PICKER: {
            const picker: TCustomizationSelectionColorPickerValue = selection;
            picker.displayValue = "";
            picker.value = "";
            break;
        }

        case CustomizationSelectionType.COLOR_SWATCH: {
            const swatch: TCustomizationSelectionColorSwatchValue = selection;
            swatch.displayValue = "";
            swatch.value = "";
            swatch.valueId = "";
            break;
        }

        case CustomizationSelectionType.PRINTED_IMAGE: {
            const image: TCustomizationSelectionImageValue = selection;
            image.displayValue = "";
            image.value = "";
            image.imageFileName = undefined;
            image.imageAttributes = undefined;
            image.merchantId = undefined;
            break;
        }

        case CustomizationSelectionType.PRINTED_TEXT: {
            const text: TCustomizationSelectionTextValue = selection;
            text.value = "";
            text.displayValue = "";
            break;
        }

        case CustomizationSelectionType.SELECTABLE: {
            const selectable: TCustomizationSelectionSelectableValue = selection;
            selectable.displayValue = NoneValueOption.name;
            selectable.value = NoneValueOption.string;
            selectable.valueId = NoneValueOption._id;
            break;
        }

        default:
            assertExhaustiveSwitch(selection);
    }
});

export const customizationSelectionUnsetDefaultNestedValues = (
    selection: TCustomizationSelection,
    attribute?: IAttribute,
): TCustomizationSelection => {
    if (!attribute || selection.type !== CustomizationSelectionType.PRINTED_IMAGE) {
        return selection;
    }

    const childAttributes = attribute.childAttributes ?? [];

    const result = { ...selection };
    const defaultNestedValues = getDefaultMetaCustomizationSelections(childAttributes, {
        parentSelection: selection,
        filterByVisibility: true,
    });

    const nestedValuesMap = mapByKey(selection.nestedValues, "attributeId");
    const defaultNestedValuesMap = mapByKey(defaultNestedValues, "attributeId");

    const nestedValues = childAttributes.map(({ _id: attributeId }) => {
        const nestedValue = nestedValuesMap.get(attributeId);
        if (nestedValue) {
            return nestedValue;
        }

        const defaultNestedValue = defaultNestedValuesMap.get(attributeId);
        if (!defaultNestedValue) {
            return undefined;
        }

        if (defaultNestedValue.type === MetaSelectionType.TEXT) {
            const text: TCustomizationSelectionTextValue = defaultNestedValue;
            text.value = "";
            text.displayValue = "";
        }

        return defaultNestedValue;
    });

    result.nestedValues = nestedValues.filter(Boolean);

    return result;
};

export const customizationSelectionValueMap = (
    selections?: TCustomizationSelection[],
    template?: ICustomizationTemplate,
): CustomizationSelectionMap => {
    const { attributeLimit, attributes = [] } = template ?? {};
    const attributesMap = mapByKey(attributes, "_id");

    const result = customizationSelectionUnsetDefaultValueMap(
        !attributeLimit || attributes.length === 1 ? attributes : attributes.filter(({ isRequired }) => isRequired),
    );

    selections?.forEach((selection) => {
        const { attributeId } = selection;
        result.set(
            attributeId,
            customizationSelectionUnsetDefaultNestedValues(selection, attributesMap.get(attributeId)),
        );
    });

    return result;
};

export const formatCustomizationSelectionMap = (values: CustomizationSelectionMap): Map<string, string> => {
    const result = new Map<string, string>();
    for (const selection of values.values()) {
        if (!selection) {
            continue;
        }

        let value = selection.displayValue ?? selection.value ?? "";
        if (selection.type === CustomizationSelectionType.PRINTED_TEXT) {
            const nestedValue = selection?.nestedValues.map((nv) => nv.displayValue ?? nv.value).join(", ");
            value += !nestedValue ? "" : ` (${nestedValue})`;
        }

        result.set(selection.label, value);
    }

    return result;
};

export const validCustomizationSelections = (values?: TCustomizationSelection[]): TCustomizationSelection[] =>
    values
        ?.filter((selection) => {
            switch (selection.type) {
                case CustomizationSelectionType.SELECTABLE:
                    const value = selection.value;
                    // TODO: update appserver
                    return !!value && value !== NoneValueOption.string;

                default:
                    return !!selection.value;
            }
        })
        .map(filterSelectionNestedValues) ?? [];

const filterSelectionNestedValues = <V extends { value?: unknown }, T extends { nestedValues?: V[] }>(selection: T) => {
    const nestedValues = selection.nestedValues;
    if (!nestedValues || nestedValues.length === 0) {
        return selection;
    }

    selection.nestedValues = nestedValues.filter((v) => !!v.value);

    return selection;
};

export const threediumModelToTemplate = (model?: IThreediumModel): IThreediumTemplate | undefined => {
    if (!model) {
        return;
    }

    return {
        distID: model.distId,
        solution3DName: model.solutionName,
        projectName: model.projectName,
        solution3DID: model.solutionId,
        version: model.version,
    };
};

/**
 * ViewCustomizationControl helper methods
 */

const getValueOption = (selectableValue: TSelectableValue): IValueOption => ({
    _id: selectableValue.valueId,
    name: selectableValue.displayValue,
    string: selectableValue.value,
});

const MetaSelectionTypeKeys = new Set([MetaSelectionType.TEXT, MetaSelectionType.SELECTABLE]);

export const isMetaNestedValue = (value: TPrintedTextChildAttributeSelection): value is TMetaCustomizationSelection =>
    MetaSelectionTypeKeys.has(value.type as MetaSelectionType);

const PrintedTextOptionValueKeys = new Set([
    PrintedTextOptionValueKey.FONT,
    PrintedTextOptionValueKey.FONT_SIZE,
    PrintedTextOptionValueKey.COLOR_SWATCH,
    PrintedTextOptionValueKey.COLOR_PICKER,
]);

export const isTextCustomization = (
    value: TPrintedTextChildAttributeSelection,
): value is TPrintedTextOptionCustomizationSelection =>
    PrintedTextOptionValueKeys.has(value.valueKey as PrintedTextOptionValueKey);

const getPrintedTextAttribute = (customizationSelection: TPrintedTextCustomizationSelection): IPrintedText => {
    const textValue = customizationSelection.displayValue;
    const printedText: IPrintedText = {
        defaultValue: textValue,
        characterLimit: { min: 1, max: textValue.length },
    };

    const printedTextSelections = customizationSelection.nestedValues.filter(isTextCustomization);

    for (const printedTextSelection of printedTextSelections) {
        switch (printedTextSelection.valueKey) {
            case PrintedTextOptionValueKey.FONT:
                printedText.font = {
                    values: [getValueOption(printedTextSelection)],
                };
                break;
            case PrintedTextOptionValueKey.FONT_SIZE:
                printedText.fontSize = {
                    values: [getValueOption(printedTextSelection)],
                };
                break;
            case PrintedTextOptionValueKey.COLOR_PICKER:
                printedText.color = {
                    picker: {
                        rgb: printedTextSelection.displayValue,
                    },
                };
                break;
            case PrintedTextOptionValueKey.COLOR_SWATCH:
                printedText.color = {
                    swatch: {
                        values: [getValueOption(printedTextSelection)],
                    },
                };
                break;
        }
    }
    return printedText;
};

const getPrintedImageAttribute = (customizationSelection: TPrintedImageCustomizationSelection): IPrintedImage => {
    return {
        permissions: [
            {
                _id: customizationSelection.imageFileName ?? uuid(),
                type: "BRAND",
                canUploadImage: false,
            },
        ],
    };
};

const getChildAttributes = (nestedValues: TMetaCustomizationSelection[]): IAttribute[] | undefined => {
    if (!nestedValues || nestedValues.length === 0) {
        return;
    }

    return nestedValues.map((nestedValue: TMetaCustomizationSelection): IAttribute => {
        const metadata: IMetadata = {};
        if (nestedValue.type === MetaSelectionType.SELECTABLE) {
            metadata.selectable = {
                values: [getValueOption(nestedValue)],
            };
        }
        if (nestedValue.type === MetaSelectionType.TEXT) {
            metadata.text = {
                defaultValue: nestedValue.displayValue,
            };
        }

        return {
            _id: nestedValue.attributeId,
            externalId: nestedValue.externalId,
            name: nestedValue.label,
            metadata,
        };
    });
};

export const getCustomizationTemplateFromSelection = (selection: TCustomizationSelection[]): ICustomizationTemplate => {
    const attributes = selection.map((customizationSelection: TCustomizationSelection): IAttribute => {
        let selectable: ISelectableValue | null = null;
        let printed: IPrintedAttribute | null = null;
        let color: IColorAttribute | null = null;
        let childAttributes: IAttribute[] | undefined;
        switch (customizationSelection.type) {
            case CustomizationSelectionType.SELECTABLE:
                selectable = {
                    values: [getValueOption(customizationSelection)],
                };
                break;
            case CustomizationSelectionType.PRINTED_TEXT:
                printed = {
                    types: [customizationSelection.printType],
                    text: getPrintedTextAttribute(customizationSelection),
                };
                break;
            case CustomizationSelectionType.PRINTED_IMAGE:
                printed = {
                    types: [customizationSelection.printType],
                    image: getPrintedImageAttribute(customizationSelection),
                };
                childAttributes = getChildAttributes(customizationSelection.nestedValues);
                break;
            case CustomizationSelectionType.COLOR_PICKER:
                color = {
                    picker: {
                        rgb: customizationSelection.displayValue,
                    },
                };
                break;
            case CustomizationSelectionType.COLOR_SWATCH:
                color = {
                    swatch: {
                        values: [getValueOption(customizationSelection)],
                    },
                };
                break;
        }

        return {
            _id: customizationSelection.attributeId,
            externalId: customizationSelection.externalId,
            name: customizationSelection.label,
            ...(childAttributes ? { childAttributes } : {}),
            ...(printed ? { printed } : {}),
            ...(color ? { color } : {}),
            ...(selectable ? { selectable } : {}),
        };
    });

    return {
        _id: uuid(),
        name: "GeneratedFromSelection",
        description: "",
        attributes,
    };
};

export const mapByKey = <T, TKey extends keyof T>(values: T[], key: TKey): Map<T[TKey], T> => {
    const map = new Map<T[TKey], T>();
    values.forEach((value) => map.set(value[key], value));

    return map;
};
